import React from 'react';
import { Modal, Menu, Box, Typography, Button, Checkbox, FormControlLabel, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import {
  ModalContainer,
  ModalHeader,
  ModalHeaderTitle,
  ModalBody,
  ModalFooter,
  TrainerListContainer,
  TrainerListItem,
  EventMenuContainer,
  EventMenuHeader,
  EventMenuBody,
  EventMenuButtonGroup,
  COLORS,
} from './PrivateLessonStyled';

/**
 * 강사 필터 모달 — 항목은 { trainerId, trainerName } 모양이면 되어 그룹 스케줄 강의실 필터도 같은 모달을 쓴다.
 * title·unit 으로 문구를, groupTitleOf 로 묶음 기준을 바꾼다(null 이면 묶지 않고 한 목록).
 */
const staffPositionOf = staff => staff.employeePositionName || null;

export const TrainerFilterModal = ({
  open,
  onClose,
  trainers,
  selectedTrainerIds,
  onTrainerCheckbox,
  onSelectAllTrainers,
  onReset,
  onApply,
  title = '임직원 필터',
  unit = '명',
  groupTitleOf = staffPositionOf,
  noGroupTitle = '직급 미지정',
}) => {
  const { t } = useTranslation();

  // 직급별 그룹핑 — 그룹명 가나다순, 직급 미지정(null)은 맨 마지막
  const byPosition = new Map();
  trainers.forEach(staff => {
    const key = groupTitleOf ? groupTitleOf(staff) : null;
    if (!byPosition.has(key)) byPosition.set(key, []);
    byPosition.get(key).push(staff);
  });
  const namedPositions = [...byPosition.keys()]
    .filter(name => name !== null)
    .sort((a, b) => a.localeCompare(b, 'ko'));
  const positionGroups = (byPosition.has(null) ? [...namedPositions, null] : namedPositions).map(name => ({
    key: name ?? 'none',
    title: name ?? noGroupTitle,
    items: byPosition.get(name),
  }));

  return (
    <Modal open={open} onClose={onClose}>
      <ModalContainer style={{ width: '400px', maxHeight: '500px' }}>
        {/* 헤더 */}
        <ModalHeader style={{ backgroundColor: '#333' }}>
          <ModalHeaderTitle style={{ color: '#fff' }}>{t(title)}</ModalHeaderTitle>
          <CloseIcon sx={{ cursor: 'pointer', color: '#fff' }} onClick={onClose} />
        </ModalHeader>

        {/* 전체 선택 */}
        <Box sx={{ p: '12px 20px', borderBottom: '1px solid #e0e0e0' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedTrainerIds.length === trainers.length && trainers.length > 0}
                indeterminate={selectedTrainerIds.length > 0 && selectedTrainerIds.length < trainers.length}
                onChange={onSelectAllTrainers}
                sx={{
                  color: COLORS.primary,
                  '&.Mui-checked': { color: COLORS.primary },
                  '&.MuiCheckbox-indeterminate': { color: COLORS.primary },
                }}
              />
            }
            label={
              <Typography sx={{ fontWeight: 600 }}>
                {t('전체 선택')} ({trainers.length}
                {unit})
              </Typography>
            }
          />
        </Box>

        {/* 임직원 목록 (직급별 그룹) */}
        <TrainerListContainer>
          {positionGroups.map(group => (
            <Box key={group.key}>
              {groupTitleOf && (
                <Typography
                  sx={{
                    p: '8px 20px 4px',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#8c8c8c',
                    backgroundColor: '#fafafa',
                  }}>
                  {group.title} ({group.items.length}
                  {unit})
                </Typography>
              )}
              {group.items.map(trainer => (
                <TrainerListItem key={trainer.trainerId}>
                  <FormControlLabel
                    sx={{ m: 0, width: '100%' }}
                    control={
                      <Checkbox
                        checked={selectedTrainerIds.includes(trainer.trainerId)}
                        onChange={() => onTrainerCheckbox(trainer.trainerId)}
                        sx={{
                          color: COLORS.primary,
                          '&.Mui-checked': { color: COLORS.primary },
                        }}
                      />
                    }
                    label={trainer.trainerName}
                  />
                </TrainerListItem>
              ))}
            </Box>
          ))}
        </TrainerListContainer>

        {/* 버튼 */}
        <ModalFooter>
          <Button
            variant="outlined"
            sx={{
              flex: 1,
              borderColor: '#d9d9d9',
              color: '#666',
              '&:hover': { borderColor: '#999', backgroundColor: '#f5f5f5' },
            }}
            onClick={onReset}>
            {t('초기화')}
          </Button>
          <Button
            variant="contained"
            sx={{
              flex: 1,
              backgroundColor: COLORS.primary,
              '&:hover': { backgroundColor: COLORS.primaryHover },
            }}
            onClick={onApply}>
            {t('적용')}
          </Button>
        </ModalFooter>
      </ModalContainer>
    </Modal>
  );
};

/**
 * 수업 취소 확인 모달
 */
export const CancelConfirmModal = ({ open, onClose, onConfirm }) => {
  const { t } = useTranslation();

  return (
    <Modal open={open} onClose={onClose}>
      <ModalContainer style={{ width: '400px', height: '200px' }}>
        <CloseIcon sx={{ position: 'absolute', top: '5px', right: '5px', cursor: 'pointer' }} onClick={onClose} />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
          }}>
          <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>{t('수업을 취소하시겠습니까?')}</Typography>
          <Box>
            <Typography sx={{ textAlign: 'center' }}>{t('*수업 취소 전 반드시 회원 및 강사에게')}</Typography>
            <Typography sx={{ textAlign: 'center' }}>{t('취소 사유를 알려주시기 바랍니다')}.</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: '20px' }}>
            <Button
              variant="contained"
              size="large"
              sx={{
                backgroundColor: '#c5c5c5',
                '&:hover': { backgroundColor: '#c5c5c5' },
                borderRadius: '10px',
              }}
              onClick={onClose}>
              {t('돌아가기')}
            </Button>
            <Button variant="contained" size="large" sx={{ borderRadius: '10px' }} onClick={onConfirm}>
              {t('수업 취소하기')}
            </Button>
          </Box>
        </Box>
      </ModalContainer>
    </Modal>
  );
};

/**
 * 출결 처리 확인 모달 — 출석/결석 모두 수강권이 차감되므로 오클릭 전에 한 번 확인받는다.
 */
export const AttendanceConfirmModal = ({ open, attendType, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const label = attendType === 'ABSENT' ? '결석' : '출석';

  return (
    <Modal open={open} onClose={onClose}>
      <ModalContainer style={{ width: '400px', height: '200px' }}>
        <CloseIcon sx={{ position: 'absolute', top: '5px', right: '5px', cursor: 'pointer' }} onClick={onClose} />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
          }}>
          <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>{t(`${label} 처리하시겠습니까?`)}</Typography>
          <Box>
            <Typography sx={{ textAlign: 'center' }}>{t(`예약된 회원 전원이 ${label} 처리됩니다`)}.</Typography>
            <Typography sx={{ textAlign: 'center' }}>{t('수강권 횟수가 차감됩니다')}.</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: '20px' }}>
            <Button
              variant="contained"
              size="large"
              sx={{
                backgroundColor: '#c5c5c5',
                '&:hover': { backgroundColor: '#c5c5c5' },
                borderRadius: '10px',
              }}
              onClick={onClose}>
              {t('돌아가기')}
            </Button>
            <Button variant="contained" size="large" sx={{ borderRadius: '10px' }} onClick={onConfirm}>
              {t(`${label} 처리`)}
            </Button>
          </Box>
        </Box>
      </ModalContainer>
    </Modal>
  );
};

/**
 * 이벤트 상세 메뉴 (팝업)
 */
export const EventDetailMenu = ({
  open,
  anchorEl,
  modalData,
  onClose,
  onAttendanceClick,
  onEditClick,
  onMemberNameClick,
  canEdit = true,
  canAttendance = true,
}) => {
  const { t } = useTranslation();
  const isConsultation = modalData?.courseClassType === 'CONSULTATION';
  // 기타 일정은 회원·강의실·참석 개념이 없다 — 제목/담당자/내용만 보여주고 수정만 연다.
  const isEtc = modalData?.courseClassType === 'ETC';
  const hasSessionInfo =
    typeof modalData?.totalSessionCount === 'number' && typeof modalData?.remainingSessionCount === 'number';

  // 상담용 색상
  const headerBgColor = COLORS.primary;
  const buttonBgColor = COLORS.primary;
  const buttonHoverBgColor = COLORS.primaryHover;
  const buttonBgLight = COLORS.primaryBg;

  return (
    <Menu
      id="event-detail-menu"
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      sx={{
        ml: 1,
        '& .MuiPaper-root': { borderRadius: '10px', overflow: 'visible !important', maxHeight: 'none !important' },
        '& .MuiMenu-list': { py: 0, maxHeight: 'none !important', overflow: 'visible !important' },
        '& .MuiPopover-paper': { maxHeight: 'none !important', overflow: 'visible !important' },
      }}>
      <EventMenuContainer>
        <EventMenuHeader sx={{ backgroundColor: headerBgColor }}>
          {!isConsultation && !isEtc && (
            <Typography sx={{ color: '#fff', fontSize: '12px' }}>
              {t('강의실')}: {modalData?.branchExerciseRoomName || '-'}
            </Typography>
          )}
          <Typography sx={{ color: '#fff', fontSize: '12px' }}>
            {modalData?.startTime} - {modalData?.endTime}
          </Typography>
        </EventMenuHeader>
        <EventMenuBody>
          <Typography sx={{ fontSize: '12px', color: '#333', mb: 0.5 }}>
            {isConsultation || isEtc ? t('담당') : t('강사')}: {modalData?.trainerName}
          </Typography>
          {isEtc && (
            <>
              <Typography sx={{ fontSize: '12px', color: '#333', mb: 0.5 }}>
                {t('제목')}: <span style={{ color: '#0F7F85', fontWeight: 600 }}>{modalData?.etcTitle}</span>
              </Typography>
              {modalData?.memo && (
                <Typography sx={{ fontSize: '12px', color: '#333', mb: 0.5, whiteSpace: 'pre-wrap' }}>
                  {t('내용')}: {modalData.memo}
                </Typography>
              )}
            </>
          )}
          {!isEtc && (
            <Typography sx={{ fontSize: '12px', color: '#333', mb: 0.5 }}>
            {t('회원')}:{' '}
            {/* 상세로 갈 수 있는 대상만 클릭을 연다. 비회원 상담은 갈 곳이 없다. */}
            {onMemberNameClick ? (
              <Box
                component="span"
                onClick={onMemberNameClick}
                sx={{
                  color: '#0F7F85',
                  fontWeight: 600,
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}>
                {modalData?.memberName}
              </Box>
            ) : (
              <span style={{ color: '#0F7F85', fontWeight: 600 }}>{modalData?.memberName}</span>
            )}
            </Typography>
          )}
          {modalData?.memberPhoneNumber && (
            <Typography sx={{ fontSize: '12px', color: '#333', mb: 0.5 }}>
              {t('연락처')}: {modalData.memberPhoneNumber}
            </Typography>
          )}
          {!isConsultation && hasSessionInfo && (
            <Typography sx={{ fontSize: '12px', color: '#333', mb: 0.5 }}>
              {t('세션')}: {modalData.remainingSessionCount}/{modalData.totalSessionCount}
            </Typography>
          )}
          <EventMenuButtonGroup>
            {!isEtc && (
            <Tooltip title={!canAttendance ? '권한이 없습니다' : ''} arrow>
              <Button
                variant="contained"
                size="small"
                sx={{
                  flex: 1,
                  backgroundColor: buttonBgColor,
                  '&:hover': { backgroundColor: buttonHoverBgColor },
                  borderRadius: '8px',
                  fontSize: '11px',
                  py: 0.5,
                  ...(!canAttendance ? { opacity: 0.5, cursor: 'default' } : {}),
                }}
                onClick={canAttendance ? onAttendanceClick : undefined}>
                {isConsultation ? '상담 관리' : '참석 관리'}
              </Button>
            </Tooltip>
            )}
            <Tooltip title={!canEdit ? '권한이 없습니다' : ''} arrow>
              <Button
                variant="outlined"
                size="small"
                sx={{
                  flex: 1,
                  borderColor: buttonBgColor,
                  color: buttonBgColor,
                  '&:hover': { borderColor: buttonHoverBgColor, backgroundColor: buttonBgLight },
                  borderRadius: '8px',
                  fontSize: '11px',
                  py: 0.5,
                  ...(!canEdit ? { opacity: 0.5, cursor: 'default' } : {}),
                }}
                onClick={canEdit ? onEditClick : undefined}>
                {isConsultation ? '상담 수정' : isEtc ? '일정 수정' : '수업 수정'}
              </Button>
            </Tooltip>
          </EventMenuButtonGroup>
        </EventMenuBody>
      </EventMenuContainer>
    </Menu>
  );
};

export default {
  TrainerFilterModal,
  CancelConfirmModal,
  EventDetailMenu,
};
